import type { Token } from '../types'

/**
 * Pure, ownership/disclosure portion of the token-visibility decision —
 * the part that does NOT depend on fog or line-of-sight geometry.
 *
 * Token visibility has exactly two GM-controlled settings:
 *  - 'visible'  (default) — a normal token. It respects fog-of-war and
 *                dynamic line-of-sight just like the map's vision rules
 *                dictate; "visible" means "not deliberately hidden", NOT
 *                "shown through walls".
 *  - 'gm_only'  — never shown to players, regardless of fog/LOS.
 *
 * An earlier iteration also let 'visible' bypass fog AND LOS outright (to fix
 * "GM dropped an NPC and nobody saw it"). That over-reached: it made every
 * default token render through walls, breaking line-of-sight for the whole
 * table (ac-vtt#131) and leaking token movement behind barriers (ac-vtt#132).
 * The correct answer is that a published token still obeys the vision geometry
 * — if the GM wants players to see something in unexplored space, they reveal
 * fog / it enters a player's line-of-sight.
 *
 * Resolution returned here:
 *  - 'shown'   — the viewer sees the token unconditionally (skip fog/LOS).
 *                GMs (see everything) and a player's own tokens.
 *  - 'hidden'  — the viewer never sees the token (skip fog/LOS). 'gm_only'.
 *  - 'fog'     — fall through to the fog/LOS geometry test. Every normal
 *                ('visible'/undefined) non-owned token for a player viewer.
 *
 * The legacy 'hidden' value (a since-removed third setting that meant
 * "follows fog/LOS") is treated as a normal 'visible' token here, so old
 * saved tokens keep working.
 */
export type VisibilityResolution = 'shown' | 'hidden' | 'fog'

export function resolveTokenDisclosure(
  token: Pick<Token, 'ownerUserId' | 'visibility'>,
  viewer: { role: string | null | undefined; userId: string | null | undefined },
): VisibilityResolution {
  // GMs see everything.
  if (viewer.role === 'gm') return 'shown'

  // Tokens the GM has explicitly marked GM-only are never shown to players.
  if (token.visibility === 'gm_only') {
    return 'hidden'
  }

  // Always see your own tokens, regardless of fog / LOS — mirrors the
  // canMoveToken ownership check so "I can control it" implies "I can see it".
  if (
    viewer.role === 'player' &&
    viewer.userId &&
    token.ownerUserId === viewer.userId
  ) {
    return 'shown'
  }

  // Every other token a player might see — 'visible', an undefined flag, or
  // the legacy 'hidden' value — obeys the map's fog/LOS geometry.
  return 'fog'
}
